import client from "@/api/client"
import {
    appointmentsResponseSchema,
    businessResponseSchema,
    incomeMetricsResponseSchema,
    businessDashboardResponseSchema,
} from "@/api/schemas/business"
import { baseResponseSchema } from "@/api/schemas/shared"

export const createBusiness = async (payload: unknown) => {
    const response = await client.post("/api/v1/business", payload)
    return baseResponseSchema.parse(response.data)
}

export const getBusiness = async (id: string) => {
    const response = await client.get(`/api/v1/business/${id}`)
    return businessResponseSchema.parse(response.data)
}

export const updateBusiness = async (payload: { id: string } & Record<string, unknown>) => {
    const { id, ...rest } = payload
    const response = await client.patch(`/api/v1/business/${id}`, rest)
    return businessResponseSchema.parse(response.data)
}

export const promoteBusinessToParent = async (id: string) => {
    const response = await client.post(`/api/v1/business/${id}/promoteToParent`)
    return businessResponseSchema.parse(response.data)
}

export const rescheduleAppointment = async (payload: unknown) => {
    const response = await client.post(
        "/api/v1/appointment/rescheduleAppointmentRequest",
        payload
    )
    return baseResponseSchema.parse(response.data)
}

export const cancelAppointment = async (payload: { appointment: string }) => {
    const response = await client.post("/api/v1/appointment/cancelAppointment", payload)
    return baseResponseSchema.parse(response.data)
}

export const completeAppointment = async (payload: { appointment: string }) => {
    const response = await client.post("/api/v1/appointment/completeAppointment", payload)
    return baseResponseSchema.parse(response.data)
}

export const markAppointmentInProgress = async (payload: { appointment: string }) => {
    const response = await client.post("/api/v1/appointment/markAppointmentInProgress", payload)
    return baseResponseSchema.parse(response.data)
}

export const acceptRescheduleAppointmentRequest = async (payload: {
    appointment: string
    newStartTime?: string
}) => {
    const response = await client.post(
        "/api/v1/appointment/acceptRescheduleAppointmentRequest",
        payload
    )
    return baseResponseSchema.parse(response.data)
}

export const cancelRescheduleAppointmentRequest = async (payload: {
    appointment: string
}) => {
    const response = await client.post(
        "/api/v1/appointment/cancelRescheduleAppointmentRequest",
        payload
    )
    return baseResponseSchema.parse(response.data)
}

export const initiateAdditionalFee = async (payload: {
    appointmentId: string
    amount: number
}) => {
    const response = await client.post("/api/v1/appointment/initiateAdditionalFee", payload)
    return baseResponseSchema.parse(response.data)
}

export const cancelAdditionalFee = async (payload: { appointmentId: string }) => {
    const response = await client.post("/api/v1/appointment/cancelAdditionalFee", payload)
    return baseResponseSchema.parse(response.data)
}

export const getAppointmentNotes = async (appointmentId: string) => {
    const response = await client.get(`/api/v1/appointment/${appointmentId}/notes`)
    return response.data as { data?: Array<{ text?: string; createdAt?: string }> }
}

export const createAppointmentNote = async (payload: {
    appointmentId: string
    text: string
}) => {
    const response = await client.post(
        `/api/v1/appointment/${payload.appointmentId}/notes`,
        { text: payload.text }
    )
    return baseResponseSchema.parse(response.data)
}

export const addBusinessBankAccount = async (payload: unknown) => {
    const response = await client.post(
        "/api/v1/business/addBusinessBankAccount",
        payload
    )
    return baseResponseSchema.parse(response.data)
}

export const requestPayout = async (payload: unknown) => {
    const response = await client.post("/api/v1/business/requestPayout", payload)
    return baseResponseSchema.parse(response.data)
}

export const getAppointments = async (params: {
    page: number
    pageSize: number
    status?: string | string[]
    search?: string
    business?: string
    service?: string
    customer?: string
}) => {
    const requestParams: Record<string, string | number | string[]> = {
        page: params.page,
        pageSize: params.pageSize,
        viewScope: "BUSINESS",
    }
    if (params.business) requestParams.business = params.business
    if (params.service) requestParams.service = params.service
    if (params.customer) requestParams.customer = params.customer
    if (params.search) requestParams.search = params.search
    if (params.status !== undefined && params.status !== "All") {
        const statuses = Array.isArray(params.status) ? params.status : [params.status]
        if (statuses.length > 0) {
            requestParams.status = statuses
        }
    }
    const response = await client.get("/api/v1/appointment", { params: requestParams })
    const parsed = appointmentsResponseSchema.safeParse(response.data)
    if (parsed.success) return parsed.data

    // Fallback for mixed backend payload shapes (e.g. populated vs id-only refs)
    // so appointment rows can still render instead of failing hard on schema mismatch.
    const payload = response.data as {
        data?: unknown
        totalPages?: number
        pageSize?: number
        totalItems?: number
        totalCount?: number
        [key: string]: unknown
    }
    return {
        ...payload,
        data: Array.isArray(payload?.data) ? payload.data : [],
    }
}

export const getAppointment = async (appointmentId: string) => {
    const response = await client.get("/api/v1/appointment", {
        params: { appointmentId, page: 1, pageSize: 1, viewScope: "BUSINESS" }
    })
    const parsed = appointmentsResponseSchema.safeParse(response.data)
    if (!parsed.success) {
        const payload = response.data as { data?: unknown; [key: string]: unknown }
        const list = Array.isArray(payload?.data) ? payload.data : []
        return {
            ...payload,
            data: list[0] ?? null,
        }
    }
    const rows = parsed.data.data
    return {
        ...parsed.data,
        data: Array.isArray(rows) ? rows[0] ?? null : null,
    }
}

export const getIncomeManagementMetrics = async (params: { id: string }) => {
    const response = await client.get("/api/v1/business/incomeManagementMetrics", {
        params: { business: params.id },
    })
    return incomeMetricsResponseSchema.parse(response.data)
}

const IMPORT_EXCEL_TIMEOUT_MS = 300_000

export const importBusinessesViaExcel = async (payload: {
    businesses: Array<Record<string, unknown>>
}) => {
    const response = await client.post("/api/v1/business/import-excel", payload, {
        timeout: IMPORT_EXCEL_TIMEOUT_MS,
    })
    return response.data
}

export const getBusinessDashboard = async (params: { business: string; months?: number }) => {
    const response = await client.get("/api/v1/analytics/business-dashboard", {
        params: {
            business: params.business,
            months: params.months || 6,
        },
    })
    return businessDashboardResponseSchema.parse(response.data)
}

export const getAvailableSlots = async (payload: {
    service: string
    startDate: string
    type?: "TIMESLOTS" | "EXACTTIME"
    provider?: string
    durationType?: string
    durationUnits?: number
}) => {
    const response = await client.post("/api/v1/appointment/available", {
        type: "TIMESLOTS",
        ...payload,
    })
    return response.data as {
        data?: {
            dates?: Array<{
                datetime?: string
                isClosed?: boolean
                availabilities?: Array<{
                    _id?: string
                    startHour?: number
                    startMinute?: number
                    endHour?: number
                    endMinute?: number
                }>
            }>
        }
    }
}

export const bookAppointmentOnBehalf = async (payload: Record<string, unknown>) => {
    const response = await client.post("/api/v1/appointment/bookOnBehalf", payload)
    return baseResponseSchema.parse(response.data)
}
